<template>
  <div class="page-container billing-dashboard-page">
    <portal to="page-name">Dashboard de faturamento</portal>
    <div class="card card-page" v-if="hasAccess">
      <div class="card-header">
        <h1 class="card-title">Dashboard de faturamento</h1>
      </div>

      <div class="card-body">
        <div class="empty db-empty" v-if="!billingEnabled">
          <div class="empty-icon"><fa-icon :icon="['fal', 'chart-line']" size="3x"/></div>
          <p class="empty-title h5">O faturamento de convênios está desligado</p>
          <p class="empty-subtitle">
            Este painel usa as guias, os lotes e as glosas do novo faturamento. Ligue o recurso
            na configuração para acompanhar os números aqui.
          </p>
          <div class="empty-action">
            <router-link class="btn btn-primary btn-icon btn-icon-left"
                         to="/financial/billing-batches/settings">
              <fa-icon :icon="['fal', 'cog']"/>Abrir a configuração
            </router-link>
          </div>
        </div>

        <template v-else>
          <dashboard-filters v-model="filter" :insurances="insurances"
                             :loading="loading" @search="search" @clear="clearFilters"/>

          <div class="loading loading-lg db-page-loading" v-if="loading && !loaded"></div>

          <div class="empty db-empty" v-else-if="error">
            <div class="empty-icon">
              <fa-icon :icon="['fal', 'exclamation-triangle']" size="3x"/>
            </div>
            <p class="empty-title h5">Não foi possível carregar o painel</p>
            <p class="empty-subtitle">{{ error }}</p>
            <div class="empty-action">
              <button class="btn btn-gray btn-icon btn-icon-left" @click="load">
                <fa-icon :icon="['fal', 'sync']"/>Tentar de novo
              </button>
            </div>
          </div>

          <template v-else>
            <overview-cards :overview="data.overview" :filter="appliedFilter"
                            :scope="scopeLabel" :loading="loading"/>

            <div class="db-split">
              <guide-status-panel :guides="data.guides" :filter="appliedFilter"
                                  :loading="loading"/>
              <batches-by-insurance :items="data.batchesByInsurance" :filter="appliedFilter"
                                    :loading="loading"/>
            </div>

            <div class="db-split">
              <glosses-by-reason :items="data.glossesByReason" :filter="appliedFilter"
                                 :loading="loading"/>
              <glosses-by-insurance :items="data.glossesByInsurance" :filter="appliedFilter"
                                    :loading="loading"/>
            </div>

            <particular-summary :particular="data.particular" :filter="appliedFilter"
                                :loading="loading"/>

            <monthly-chart :items="data.monthly" :competence="appliedFilter.competence"
                           :loading="loading"/>
          </template>
        </template>
      </div>

      <div class="card-footer" v-if="showFooter">
        <span class="db-footer-item" v-for="item in footerItems" :key="item.label">
          {{ item.label }}: <strong class="text-primary">{{ item.value | currency }}</strong>
        </span>
      </div>
    </div>
    <forbidden v-else></forbidden>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { LIST_BILLINGS } from '@/data/actions/modules/financial';
import { isEnabled as isInsuranceBillingEnabled } from '@/app/billing/guide/service';
import Forbidden from '@/components/auth/Forbidden.vue';
import DashboardFilters from './DashboardFilters.vue';
import OverviewCards from './OverviewCards.vue';
import GuideStatusPanel from './GuideStatusPanel.vue';
import BatchesByInsurance from './BatchesByInsurance.vue';
import GlossesByReason from './GlossesByReason.vue';
import GlossesByInsurance from './GlossesByInsurance.vue';
import ParticularSummary from './ParticularSummary.vue';
import MonthlyChart from './MonthlyChart.vue';
import {
  blankData, competenceRange, competenceTitle, currentCompetence, normalize,
} from '../helpers';

function blankFilter() {
  return {
    competence: currentCompetence(),
    insurancePlanId: '',
  };
}

export default {
  components: {
    Forbidden,
    DashboardFilters,
    OverviewCards,
    GuideStatusPanel,
    BatchesByInsurance,
    GlossesByReason,
    GlossesByInsurance,
    ParticularSummary,
    MonthlyChart,
  },
  data() {
    const filter = blankFilter();
    return {
      loading: false,
      loaded: false,
      error: null,
      insurances: [],
      filter,
      appliedFilter: { ...filter },
      data: blankData(),
    };
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
    hasAccess() {
      return this.$can(LIST_BILLINGS);
    },
    billingEnabled() {
      return isInsuranceBillingEnabled(this.user);
    },
    showFooter() {
      return this.billingEnabled && this.loaded && !this.error;
    },
    footerItems() {
      const { production, billed, expected } = this.data.overview;
      return [
        { label: 'Produção', value: production.value },
        { label: 'Faturado', value: billed.value },
        { label: 'A receber', value: expected.value },
      ];
    },
    appliedInsuranceName() {
      const found = this.insurances
        .find(item => String(item.id) === String(this.appliedFilter.insurancePlanId));
      return found ? found.name : '';
    },
    scopeLabel() {
      const parts = [competenceTitle(this.appliedFilter.competence)];
      parts.push(this.appliedInsuranceName || 'Todos os convênios');
      return parts.filter(Boolean).join(' · ');
    },
  },
  mounted() {
    if (!this.hasAccess || !this.billingEnabled) return;
    this.loadInsurances();
    this.load();
  },
  methods: {
    params(filter) {
      const params = competenceRange(filter.competence);
      if (filter.insurancePlanId) params.insurancePlanId = filter.insurancePlanId;
      return params;
    },
    search() {
      return this.load();
    },
    clearFilters() {
      this.filter = blankFilter();
      return this.load();
    },
    load() {
      this.loading = true;
      this.error = null;
      const applied = { ...this.filter };
      return this.$http.get('/billing-dashboard', { params: this.params(applied) })
        .then(({ data }) => {
          this.appliedFilter = applied;
          this.data = normalize(data);
          this.loaded = true;
        })
        .catch((e) => {
          this.error = e.response && e.response.data && e.response.data.message
            ? e.response.data.message
            : 'Verifique a conexão e tente novamente.';
          this.$toast.error(e);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadInsurances() {
      return this.$http.get('/insurance-plans', { params: { limit: 0, active: true } })
        .then(({ data }) => {
          this.insurances = data.items
            .filter(item => item.plan && item.plan.type !== 'particular')
            .map(item => ({ id: item.plan.id, name: item.customName }));
        })
        .catch(() => {});
    },
  },
};
</script>

<style lang="scss">
@import "../dashboard.scss";
</style>
